<script lang="ts">
import '@banno/jha-wc/src/buttons/jha-button/jha-button.js'
import {defineComponent, type PropType} from 'vue'
import {DisclosureConstant} from '@/common/constant/DisclosureConstant'
import BroadcastService from '@/common/services/Broadcast/BroadcastService'
import LanguageFactory from '@/common/services/Language/LanguageFactory'
import {$inj} from '@/common/decorators/depinject'
import {OpenLanguageKeysConstant} from '@/common/constant/OpenLanguageKeysConstant'
import type {IErrorMessage} from '@/common/models/IErrorMessage'
import bltNotification from '@/common/components/bltNotification/bltNotification.vue'
import type {BltIframeData} from '@/common/directives/bltIframe/BltIframeData'
import type {Disclosure} from '@/common/models/IDisclosure'
import BltModalDialog from '@/common/components/bltDialog/bltModalDialog.vue'
import {VsgProgress} from '@jack-henry/jha-vue-components'
import type {GuidedDisclosureState} from '@/common/directives/bltIframe/guidedDisclosure'
import {useLDFlag} from 'launchdarkly-vue-client-sdk'

const defaultGuidedDisclosureState = (): GuidedDisclosureState => ({
  enabled: false,
  complete: true,
  totalSteps: 0,
  completedSteps: 0,
  currentStepNumber: 0,
  currentStepLabel: ''
})

type DisclosureIframeRef = HTMLIFrameElement & {
  getDictionary: () => BltIframeData
  goToNextRequired: () => void
}

export default defineComponent({
  name: 'disclosureDialog',
  emits: ['disclosureClosed', 'acceptDisclosure'],
  components: {
    VsgProgress,
    BltModalDialog,
    bltNotification
  },
  props: {
    disclosure: {
      required: true,
      type: Object as PropType<Disclosure>
    },
    secondaryTitle: {
      required: false,
      type: String
    },
    showFooter: {
      required: false,
      type: Boolean,
      default: true
    },
    showDisclosureDialog: {
      required: true,
      type: Boolean
    },
    isFinalDisclosure: {
      type: Boolean,
      default: false
    },
    isAccountApplicantDisclosure: {
      type: Boolean,
      default: false
    },
    isLastDisclosureToAccept: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    errorMessages: {
      type: Array<IErrorMessage>,
      required: false,
      default: []
    }
  },
  data() {
    return {
      DisclosureConstant,
      guidedDisclosureState: defaultGuidedDisclosureState()
    }
  },
  setup() {
    const languageFactory = $inj(LanguageFactory)
    const broadcastService = $inj(BroadcastService)
    const guidedDisclosureEnabled = useLDFlag('oa-guided-disclosure', false)
    return {
      languageFactory,
      broadcastService,
      guidedDisclosureEnabled
    }
  },
  watch: {
    guidedDisclosureEnabled(isEnabled: boolean) {
      if (!isEnabled) {
        this.guidedDisclosureState = defaultGuidedDisclosureState()
      }
    }
  },
  computed: {
    acceptText() {
      if (this.isFinalDisclosure) {
        return this.isLastDisclosureToAccept
          ? this.languageFactory.getSynchronous(OpenLanguageKeysConstant.OPEN_FINAL_DISCLOSURE_BUTTON_ACCEPT)
          : // To Do -> Use the text from the server when API is ready
            'Accept and Next'
      }
      if (this.isAccountApplicantDisclosure) {
        return this.languageFactory.getSynchronous(
          OpenLanguageKeysConstant.OPEN_ACCOUNT_APPLICANT_DISCLOSURE_BUTTON_ACCEPT
        )
      }
      return this.isLastDisclosureToAccept
        ? this.languageFactory.getSynchronous(OpenLanguageKeysConstant.OPEN_INITIAL_DISCLOSURE_BUTTON_SUBMIT)
        : // To Do -> Use the text from the server when API is ready
          'Accept and Next'
    },
    disclosureStatus() {
      return this.disclosure.status === DisclosureConstant.COMPLETE
    },
    isGuidedIncomplete() {
      return this.guidedDisclosureEnabled && this.guidedDisclosureState.enabled && !this.guidedDisclosureState.complete
    },
    isAcceptDisabled() {
      return this.loading || this.disclosureStatus || this.isGuidedIncomplete
    },
    guidedProgressText() {
      if (!this.guidedDisclosureEnabled || !this.guidedDisclosureState.enabled) {
        return ''
      }

      if (this.guidedDisclosureState.complete) {
        return '✓ All required steps complete. Click Accept to continue.'
      }

      return `Progress: Step ${this.guidedDisclosureState.currentStepNumber} of ${this.guidedDisclosureState.totalSteps} - ${this.guidedDisclosureState.currentStepLabel}`
    }
  },
  methods: {
    getDisclosureIframe(): DisclosureIframeRef | undefined {
      return this.$refs.disclosureContent as DisclosureIframeRef | undefined
    },
    getBltIframeData(): BltIframeData | undefined {
      return this.getDisclosureIframe()?.getDictionary()
    },
    acceptDisclosure() {
      if (this.loading) {
        return
      }

      const bltIframeData = this.getBltIframeData()
      if (!bltIframeData) {
        return
      }

      this.$emit('acceptDisclosure', bltIframeData)
    },
    onGuidedDisclosureState(event: Event) {
      if (!this.guidedDisclosureEnabled) {
        return
      }

      const customEvent = event as CustomEvent<GuidedDisclosureState>
      this.guidedDisclosureState = customEvent.detail
    },
    goToNextRequired() {
      this.getDisclosureIframe()?.goToNextRequired?.()
    },
    closeDisclosure() {
      this.$emit('disclosureClosed')
    }
  }
})
</script>

<template>
  <blt-modal-dialog
    :disclosureHeightHotfix="true"
    @cancel="closeDisclosure"
    :show-footer="showFooter"
    :showDialog="showDisclosureDialog"
    :aria-label="disclosure.disclosureTitle"
    preventAutoClose
    type="large-dialog">
    <template v-slot:dialog-header>
      <div class="w-100">
        <h1>{{ disclosure.disclosureTitle }}</h1>
        <h2>{{ secondaryTitle }}</h2>
      </div>
      <jha-button
        icon
        type="button"
        @click="closeDisclosure"
        class="reset">
        <jha-icon-close />
      </jha-button>
    </template>
    <template v-slot:dialog-content>
      <div
        style="overflow: hidden"
        class="w-100 container">
        <div
          v-if="errorMessages.length !== 0"
          class="blt-notification-wrapper pb-12"
          id="workflowNotificationScroll">
          <blt-notification :messages="errorMessages" />
        </div>
        <div
          v-if="guidedDisclosureEnabled && guidedDisclosureState.enabled"
          :class="[
            'guided-progress-banner',
            {'guided-progress-banner-complete': guidedDisclosureState.complete}
          ]">
          {{ guidedProgressText }}
        </div>
        <iframe
          v-if="!loading"
          id="disclosureId"
          title="disclosure"
          class="disclosure-content-iframe"
          ref="disclosureContent"
          @guided-disclosure-state="onGuidedDisclosureState"
          v-blt-iframe="{
            html: disclosure.disclosure,
            dictionary: disclosure.data,
            disclosureStatus: disclosure.status,
            guidedDisclosureEnabled: guidedDisclosureEnabled
          }">
        </iframe>
        <vsg-progress
          v-if="loading"
          card />
      </div>
    </template>
    <template v-slot:dialog-footer>
      <div class="disclosure-footer-actions">
        <jha-button
          v-if="isGuidedIncomplete"
          primary
          sync
          class="next-required-button"
          @click="goToNextRequired">
          Next Required
        </jha-button>
        <jha-button
          primary
          sync
          @click="acceptDisclosure"
          :disabled="isAcceptDisabled">
          {{ acceptText }}
        </jha-button>
      </div>
    </template>
  </blt-modal-dialog>
</template>

<style scoped>
.container {
  min-height: calc(86vh - 150px);
  display: flex;
  flex-direction: column;
}

.disclosure-content-iframe {
  width: 100%;
  border: 0;
  overflow: hidden;
  flex-grow: 1;
}

.disclosure-content-iframe input {
  width: 100%;
}

.guided-progress-banner {
  background: linear-gradient(135deg, #fff5d6 0%, #ffefbf 100%);
  border: 1px solid #e0b647;
  box-shadow: 0 10px 24px rgba(176, 131, 24, 0.12);
  color: #6a4b00;
  border-radius: 10px;
  margin-bottom: 12px;
  padding: 12px 16px;
  font-size: 14px;
  line-height: 20px;
  font-weight: 700;
}

.guided-progress-banner-complete {
  background: linear-gradient(135deg, #ebf8ef 0%, #d9f1e1 100%);
  border-color: #4aa367;
  box-shadow: 0 10px 24px rgba(52, 125, 74, 0.12);
  color: #114d24;
}

.disclosure-footer-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.next-required-button {
  min-width: 140px;
}

h2 {
  color: var(--jha-text-base);
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  text-align: center;
  margin: 0;
}

@media (max-width: 599px) {
  .disclosure-content-iframe {
    min-height: calc(82vh - 90px);
  }
}
@media (max-width: 375px) {
  .disclosure-content-iframe {
    min-height: calc(77vh - 90px);
  }
}
@media (max-width: 360px) {
  .disclosure-content-iframe {
    min-height: calc(76vh - 90px);
  }
}
</style>
